import React, { useState, useEffect } from "react";
import data from "./newcoutries.json";
import Select from "react-select";
import { useTheme } from "next-themes";
import { useLang } from "hooks/useLang";
import convertArNumbersToEn from "utils/helpers/convertArNumbersToEn";

interface IProps {
  country: string;
  setCountry: (country: string) => void;
  phone: string | any;
  setPhone: (phone: string) => void;
  required: boolean;
  customMessage?: string;
  placeholder?: string;
  defaultInputValue?: string;
  id?: string;
  name?: string;
  className?: string;
  autoFocus?: boolean;
  error?: string;
  readOnly?: boolean;
}

const CustomPhoneInput = ({ ...props }: IProps) => {
  const { theme } = useTheme();
  const { t, isAr } = useLang();
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleCountryChange = (e: any) => {
    if (isNaN(Number(e.value))) {
      return;
    }
    props.setCountry(convertArNumbersToEn(e.value));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isNaN(Number(e.target.value))) {
      return;
    }
    props.setPhone(convertArNumbersToEn(e.target.value.trim()));
  };

  let parse = Object.values([data]).map((item) => item);
  // this is the object structure
  // {AX:{name:"Aland Islands",dial_code:"+358",code:"AX"}}
  // remove the AX and get the object
  var countries = parse[0];
  // get the values of the object
  var values = Object.values(countries);

  const customSelectStyles = {
    control: (provided: any) => ({
      ...provided,
      width: 130,
      height: 36,
      border: "none",
      boxShadow: "none",
      "&:hover": {
        border: "none",
        boxShadow: "none",
      },
      backgroundColor: "transparent",
      color: theme === "dark" ? "#fff !important" : "black !important",
      // display: "flex",
      // flexDirection: isAr ? "row-reverse" : "row",
    }),
    option: (provided: any) => ({
      ...provided,
      color: theme === "dark" ? "#fff" : "rgb(75, 85, 99)",
      fontSize: 10,
      backgroundColor: theme === "dark" ? "#392986" : "#ffffff",
      "&:hover": {
        backgroundColor: theme === "dark" ? "#1D1544" : "#eeeeee",
      },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: theme === "dark" ? "#fff" : "rgb(75, 85, 99)",
      fontSize: 10,
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: theme === "dark" ? "#fff" : "rgb(75, 85, 99)",
      "&:hover": {
        color: "black",
      },
    }),
    input: (provided: any) => ({
      ...provided,
      color: theme === "dark" ? "#fff" : "rgb(75, 85, 99)",
      fontSize: 10,
    }),
  };

  useEffect(() => {
    if (props.phone.length >= 4) {
      if (props.phone[0] === "0") {
        props.setPhone(props.phone.substring(1));
      }
    }
  }, [props.phone]);

  useEffect(() => {
    if (props.country[0] === "+") {
      props.setCountry(props.country.substring(1));
    }
  }, [props.country]);

  return (
    <div className="flex w-full flex-col items-center">
      <div
        className={`flex h-9 ${
          isAr ? "flex-row-reverse" : "flex-row"
        } w-full rounded-md border transition-all ${
          isFocused ? "border-blue-500" : "border-gray-300"
        } ${
          props.error ? "errorStyle" : ""
        } bg-white bg-opacity-40 text-sm shadow-sm dark:bg-primary-theme-500 dark:bg-opacity-20 dark:text-white`}
      >
        <Select
          styles={customSelectStyles}
          options={values}
          onChange={handleCountryChange}
          tabIndex={-1} // This skips the input on tabbing
          isDisabled={props.readOnly}
          placeholder={
            <span className="truncate text-xs text-black dark:text-white">
              {t("select_country_code")}
            </span>
          }
          defaultValue={{
            label: values[0].label,
            value: values[0].value,
          }}
          // saudi arabia is the default country and its index in the array is 191
          // defaultInputValue={saudiArabia.value + saudiArabia.country}
          className="text-clip text-xs"
          formatOptionLabel={(option: any) => {
            return (
              <div
                className={`flex items-center justify-center gap-2 ${
                  isAr ? "flex-row-reverse" : "flex-row"
                }`}
              >
                <span className="text-end text-[10px]">({option.value})</span>
                <div
                  className="flex h-4 w-4 justify-center"
                  dangerouslySetInnerHTML={{
                    __html: option.label,
                  }}
                ></div>
              </div>
            );
          }}
        />

        <input
          id={props.id}
          name={props.name}
          autoComplete="off"
          className={`w-full bg-transparent placeholder:px-4 ${
            isAr
              ? "text-end placeholder:text-right"
              : "text-start placeholder:text-left"
          } ${props.className}`}
          type="text"
          value={props.phone}
          onChange={handlePhoneChange}
          maxLength={9}
          autoFocus={props.autoFocus}
          onFocus={handleFocus}
          onBlur={handleBlur}
          readOnly={props.readOnly}
          inputMode="numeric"
        />
      </div>

      <span className="text-sm text-red-500">
        {props.customMessage && props.customMessage}
      </span>
    </div>
  );
};

export default CustomPhoneInput;

CustomPhoneInput.displayName = "CustomPhoneInput";
// CustomPhoneInput.defaultProps = {
//     country: "966",
//     phone: "",
//     required: false,
//     placeholder: "Phone number",
// }

export type { IProps as CustomPhoneInputProps };
